import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePagesStatus } from '../hooks/usePagesStatus';
import { Menu, X, Star, User, LogOut, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { status: pagesStatus, loading: pagesLoading } = usePagesStatus();

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/categories', label: 'Catégories' },
    { path: '/proposer', label: 'Proposer', highlight: true },
    { path: '/voter', label: 'Voter' },
    { path: '/gala', label: 'Le Gala' },
    { path: '/actualites', label: 'Actualités' },
    { path: '/resultats', label: 'Résultats' },
    { path: '/partenaires', label: 'Partenaires' },
  ];

  const visibleNavLinks = navLinks.filter((link) => {
    if (['/'].includes(link.path)) {
      return true;
    }
    if (pagesLoading || !pagesStatus) {
      return true;
    }

    const pageKey = link.path.slice(1);
    return pagesStatus[pageKey] !== false;
  });

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 w-full z-50 border-b border-white/5 bg-obsidian/80 backdrop-blur-xl" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="logo-link">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gold flex items-center justify-center group-hover:animate-glow-pulse transition-all">
              <Star className="w-4 h-4 md:w-5 md:h-5 text-obsidian" />
            </div>
            <div className="hidden sm:block">
              <span className="font-syne font-bold text-lg text-white tracking-tight">STERED AN NOZ</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden xl:flex items-center gap-1">
            {visibleNavLinks.slice(0, 6).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.slice(1) || 'home'}`}
                className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${
                  link.highlight
                    ? 'bg-gold/10 text-gold hover:bg-gold/20'
                    : isActive(link.path)
                    ? 'text-gold'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* More dropdown */}
            {visibleNavLinks.length > 6 && (
              <DropdownMenu>
                <DropdownMenuTrigger className="px-3 py-2 text-xs uppercase tracking-wider text-white/60 hover:text-white flex items-center gap-1">
                  Plus <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-obsidian-light border-white/10">
                  {visibleNavLinks.slice(6).map((link) => (
                    <DropdownMenuItem key={link.path} asChild>
                      <Link 
                        to={link.path} 
                        className={`text-white hover:text-gold cursor-pointer ${isActive(link.path) ? 'text-gold' : ''}`}
                      >
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 border border-white/20 hover:border-gold/50 transition-colors" data-testid="user-menu-trigger">
                    <User className="w-4 h-4 text-gold" />
                    <span className="text-sm text-white max-w-[100px] truncate">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-obsidian-light border-white/10">
                  {user.is_admin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="text-white hover:text-gold cursor-pointer" data-testid="admin-link">
                        Admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} className="text-white hover:text-gold cursor-pointer" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/auth"
                className="bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-4 py-2 text-xs transition-all"
                data-testid="login-btn"
              >
                Connexion
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="xl:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <div className="xl:hidden py-4 border-t border-white/5 animate-fade-in max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              {visibleNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-3 text-sm ${
                    link.highlight
                      ? 'bg-gold/10 text-gold col-span-2 text-center font-bold'
                      : isActive(link.path) 
                      ? 'text-gold bg-white/5' 
                      : 'text-white/70'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-white/5">
              {user ? (
                <div className="flex items-center justify-between px-4">
                  <span className="text-white">{user.name}</span>
                  <button onClick={() => { logout(); setIsOpen(false); }} className="text-red-400 text-sm">
                    Déconnexion
                  </button>
                </div>
              ) : (
                <Link 
                  to="/auth" 
                  onClick={() => setIsOpen(false)} 
                  className="block bg-gold text-obsidian font-bold uppercase tracking-widest px-4 py-3 text-sm text-center mx-4"
                >
                  Connexion
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
